import { useEffect, useMemo, useRef, useState } from 'react';
import { SelectInput } from 'stubben-edge-unified-frontend-react-components';
import type { SelectOptionProp } from '@ui/SelectField/types';
import type { DataTableProviderFilterItem } from './types';
import { DATATABLE_FILTER_NO_RESULTS_TEXT } from './datatableFilterText';

type DatatableFilterSelectInputProps = {
  filter: DataTableProviderFilterItem;
  label: string;
  value: string | number | '';
  disabled?: boolean;
  testIdentifier: string;
  onChange: (
    value: string | number | string[],
    option?: SelectOptionProp
  ) => void;
};

function parseSelectedValues(value: string | number | ''): Array<string | number> {
  if (value === '' || value == null) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const asNumber = Number(part);
      return Number.isNaN(asNumber) ? part : asNumber;
    });
}

export function DatatableFilterSelectInput({
  filter,
  label,
  value,
  disabled = false,
  testIdentifier,
  onChange
}: DatatableFilterSelectInputProps) {
  const isMulti = Boolean(filter.multiSelect);
  const [options, setOptions] = useState<SelectOptionProp[]>(
    filter.selectOptions ?? []
  );
  const [loading, setLoading] = useState(!!filter.selectOptionsResolver);
  // A failed search is not "no results", so it hides the no-results message.
  const [searchFailed, setSearchFailed] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestRef = useRef(0);

  useEffect(() => {
    if (filter.selectOptions) {
      setOptions(filter.selectOptions);
      setLoading(false);
      return;
    }

    if (!filter.selectOptionsResolver) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    // A search started after this load owns the options and loading flag.
    const searchAtStart = searchRequestRef.current;
    const searchedSince = () => searchRequestRef.current !== searchAtStart;
    setLoading(true);

    filter
      .selectOptionsResolver()
      .then((data) => {
        if (!cancelled && !searchedSince()) {
          setOptions(data ?? []);
        }
      })
      .catch(() => {
        if (!cancelled && !searchedSince()) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled && !searchedSince()) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filter.selectOptions, filter.selectOptionsResolver]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const selectedOptions = useMemo(
    () => parseSelectedValues(value),
    [value]
  );

  const selectedLabel = useMemo(() => {
    if (value === '' || value == null) return '';
    const match = options.find(
      (option) => String(option.value) === String(value)
    );
    return match?.label ?? String(value);
  }, [options, value]);

  const [searchText, setSearchText] = useState(selectedLabel);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) {
      setSearchText(selectedLabel);
    }
  }, [selectedLabel]);

  const handleSearchTextChange = (text: string) => {
    isEditingRef.current = true;
    setSearchText(text);
    if (!filter.searchSelectOptions) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    // Only the latest search may update the options; an earlier, slower
    // response must not overwrite it. Loading starts now, not after the
    // debounce, so the previous term's results or "No results found" aren't
    // shown for text that hasn't been searched yet.
    const request = ++searchRequestRef.current;
    const isLatest = () => request === searchRequestRef.current;
    setLoading(true);
    searchTimerRef.current = setTimeout(() => {
      filter
        .searchSelectOptions?.(text)
        .then((data) => {
          if (!isLatest()) return;
          if (data) setOptions(data);
          setSearchFailed(false);
        })
        .catch(() => {
          if (!isLatest()) return;
          setOptions([]);
          setSearchFailed(true);
        })
        .finally(() => {
          if (isLatest()) setLoading(false);
        });
    }, 250);
  };

  const noResultsText = searchFailed
    ? undefined
    : DATATABLE_FILTER_NO_RESULTS_TEXT;
  const disableOptionFiltering = !!filter.searchSelectOptions;

  if (isMulti) {
    return (
      <SelectInput
        multi
        label={label}
        placeholder={filter.placeholder || filter.label}
        options={options}
        loading={loading}
        disabled={disabled}
        noResultsText={noResultsText}
        disableOptionFiltering={disableOptionFiltering}
        selectedOptions={selectedOptions}
        enableClearButton
        handleTextInputOnChange={
          filter.searchSelectOptions ? handleSearchTextChange : undefined
        }
        onSelectOption={(selectedValue) => {
          const option = options.find(
            (o) => `${o.value}` === `${selectedValue}`
          );
          onChange(selectedValue as string | number, option);
        }}
        onRemoveOption={(removedValue) => {
          const option = options.find((o) => `${o.value}` === `${removedValue}`);
          onChange(removedValue as string | number, option);
        }}
        onClearOptions={() => onChange('', undefined)}
        testIdentifier={testIdentifier}
      />
    );
  }

  return (
    <SelectInput
      label={label}
      placeholder={filter.placeholder || filter.label}
      options={options}
      loading={loading}
      disabled={disabled}
      noResultsText={noResultsText}
      disableOptionFiltering={disableOptionFiltering}
      value={searchText}
      onChange={handleSearchTextChange}
      selectedOption={value === '' ? undefined : value}
      handleTextInputOnChange={
        filter.searchSelectOptions ? handleSearchTextChange : undefined
      }
      onSelectOption={(selectedValue) => {
        const option = options.find((o) => `${o.value}` === `${selectedValue}`);
        isEditingRef.current = false;
        onChange(selectedValue as string | number, option);
        setSearchText(option?.label ?? String(selectedValue));
      }}
      onRemoveOption={() => {
        isEditingRef.current = false;
        onChange('', undefined);
        setSearchText('');
      }}
      testIdentifier={testIdentifier}
    />
  );
}
